import React from "react";
import { ChevronLeft, ChevronDown } from "lucide-react";
import { ResponsiveData } from "@/types/models/page";
import {
  ComponentConfig,
  ComponentDefinition,
} from "@/types/models/componentConfig";
import { DeviceType } from "@/services/sectionDataService";
import { DynamicInput } from "../../ui/elements/DynamicInput";

interface NestedPanelProps {
  componentKey: string | null;
  componentData?: ResponsiveData;
  componentDefinitions: ComponentDefinition[];
  onBack: () => void;
  formValues: Record<string, unknown>;
  onInputChange: (fieldKey: string, value: unknown) => void;
  currentDevice: DeviceType;
  onDeviceChange: (newDevice: DeviceType) => void;
  isLocked?: boolean;
  layoutMode?: "grid" | "vertical";
  showDeviceTabs?: boolean;
}

const NestedPanel: React.FC<NestedPanelProps> = ({
  componentKey,
  componentData,
  componentDefinitions,
  onBack,
  formValues,
  onInputChange,
  currentDevice,
  onDeviceChange,
  isLocked = false,
  layoutMode = "vertical",
  showDeviceTabs = true,
}) => {
  const showContent = componentKey && componentData;
  const backendComponentDef = componentDefinitions.find(
    (def) => def.slug === componentData?.componentSlug,
  );

  console.log("[DEBUG-shopbyicons] NestedPanel render:", {
    componentKey,
    showContent,
    hasComponentData: Boolean(componentData),
    componentSlug: componentData?.componentSlug,
    componentName: componentData?.name,
    desktopIsArray: Array.isArray(componentData?.desktop),
    desktopKeys: !Array.isArray(componentData?.desktop) && componentData?.desktop
      ? Object.keys(componentData.desktop as Record<string, unknown>)
      : [],
    backendDefinitionFound: Boolean(backendComponentDef),
    backendDefinitionSlug: backendComponentDef?.slug,
    backendConfigCount: backendComponentDef?.config?.length || 0,
  });

  const inferredComponentDef =
    componentData
      ? {
          id: `${componentData.componentSlug || "unknown"}-inline` as string,
          name:
            componentData.name || componentData.componentSlug || "Component",
          slug: componentData.componentSlug || "unknown",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          config: (Array.isArray(componentData.desktop)
            ? componentData.desktop
            : []
          ).map((input: any) => {
            const backendMatch = backendComponentDef?.config?.find(
              (config) =>
                (config.input_key || config.title) === input?.inputKey ||
                config.title === input?.inputKey,
            );

            return {
              title:
                backendMatch?.title ||
                input?.inputKey ||
                "unknown",
              description:
                backendMatch?.description ||
                `${input?.inputKey || "field"} configuration`,
              input_key: input?.inputKey || backendMatch?.input_key || null,
              type: input?.type || backendMatch?.type || "text",
              accepted_values: backendMatch?.accepted_values || null,
              accepted_range: backendMatch?.accepted_range || null,
              default_value:
                input && "value" in input ? input.value : backendMatch?.default_value,
              schema_version: backendMatch?.schema_version,
              status: backendMatch?.status,
              value_kind: backendMatch?.value_kind,
            };
          }) as ComponentConfig[],
        }
      : undefined;

  const componentDef = inferredComponentDef || backendComponentDef;

  if (!componentDef) {
    console.error("[DEBUG-shopbyicons] No component definition could be resolved:", {
      componentKey,
      componentData,
      componentDefinitionsCount: componentDefinitions.length,
    });
  } else {
    console.log("[DEBUG-shopbyicons] Using component definition:", {
      componentKey,
      slug: componentDef.slug,
      configCount: componentDef.config?.length || 0,
      source: inferredComponentDef ? "inferred" : "backend",
    });
  }

  return (
    <div
      className="flex flex-col h-full"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {showContent ? (
        <>
          <div className="flex items-center p-4 border-b border-border dark:border-auth-background">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBack();
              }}
              className="mr-2 p-1 rounded-full hover:bg-muted"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h3 className="font-semibold">{componentData.name}</h3>
              <p className="text-xs text-muted-foreground">
                {componentData.description}
              </p>
            </div>
          </div>

          {isLocked && (
            <div className="p-4 border-b border-border dark:border-auth-background">
              <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <ChevronDown className="w-4 h-4 text-amber-600" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Section is locked. Unlock it to edit properties.
                </p>
              </div>
            </div>
          )}

          {showDeviceTabs && (
            <div className="p-4 border-b border-border dark:border-auth-background">
              <div className="flex space-x-1 bg-muted dark:bg-auth-background p-1 rounded-lg">
                {(["desktop", "tablet", "mobile"] as DeviceType[]).map(
                  (device) => (
                    <button
                      key={device}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isLocked) {
                          onDeviceChange(device);
                        }
                      }}
                      disabled={isLocked}
                      className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-colors ${
                        isLocked
                          ? "opacity-50 cursor-not-allowed"
                          : currentDevice === device
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {device.charAt(0).toUpperCase() + device.slice(1)}
                    </button>
                  ),
                )}
              </div>
            </div>
          )}

          <div
            className={`flex-1 p-4 overflow-y-auto ${isLocked ? "pointer-events-none opacity-50" : ""}`}
          >
            {componentDef ? (
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {layoutMode === "grid" ? (
                  <div className="grid grid-cols-3 gap-4">
                    {componentDef.config.map((config) => {
                      const inputKey = config.input_key || config.title;
                      const fieldKey = `${componentDef.slug}::${inputKey}`;
                      const currentValue = formValues[fieldKey];
                      const type = (config.type || "").toLowerCase();
                      const wrapperClass =
                        type.includes("wysiwyg") ||
                        type.includes("richtext") ||
                        type.includes("textarea")
                          ? "col-span-3"
                          : type === "text" ||
                              type === "number" ||
                              type === "select" ||
                              type === "url"
                            ? "col-span-1"
                            : "col-span-2";
                      return (
                        <div key={fieldKey} className={wrapperClass}>
                          <DynamicInput
                            config={config}
                            value={currentValue}
                            onChange={(value) => {
                              if (!isLocked) {
                                onInputChange(fieldKey, value);
                              }
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {componentDef.config.map((config) => {
                      const inputKey = config.input_key || config.title;
                      const fieldKey = `${componentDef.slug}::${inputKey}`;
                      const currentValue = formValues[fieldKey];
                      if (!config.type) {
                        console.warn("[DEBUG-shopbyicons] Config missing type:", {
                          componentKey,
                          fieldKey,
                          config,
                        });
                      }
                      return (
                        <DynamicInput
                          key={fieldKey}
                          config={config}
                          value={currentValue}
                          onChange={(value) => {
                            if (!isLocked) {
                              onInputChange(fieldKey, value);
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No configuration found for this component.
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground text-sm">
            No component selected.
          </p>
        </div>
      )}
    </div>
  );
};

export default NestedPanel;
